<template>
	<nav class="equipment-menu">
		<ul>
			<li v-for="(item, idx) in menu" :key="item.id" class="menu-parent">
				<div
					class="parent-row"
					:class="{ hover: isCurrentItem(item) }"
					@click="toggle(idx)"
				>
					<span class="parent-title"><span v-html="item.title"></span></span>

					<span class="arrow" :class="{ open: openIndex === idx }"
						><svg
							width="16"
							height="10"
							viewBox="0 0 16 10"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M0 1.5303L1.48402 -0.00683594L8.17667 6.91889L14.5146 0.360243L16 1.89738L8.17667 9.99316L0 1.5303Z"
								fill="#53628B"
							/>
						</svg>
					</span>
				</div>
				<ul v-if="openIndex === idx && item.children" class="submenu">
					<li v-for="child in item.children" :key="child.id" class="menu-child">
						<div class="child-row">
							<a
								:href="child.url"
								class="child-title"
								:class="{ hover: isCurrentItem(child) }"
								><span v-html="child.title"></span
							></a>
						</div>
						<ul v-if="child.children" class="subsubmenu">
							<li
								v-for="subchild in child.children"
								:key="subchild.id"
								class="menu-subchild"
							>
								<a
									:href="subchild.url"
									class="subchild-title"
									:class="{ hover: isCurrentItem(subchild) }"
									><span v-html="subchild.title"></span
								></a>
							</li>
						</ul>
					</li>
				</ul>
			</li>
		</ul>
	</nav>
</template>

<script>
export default {
	name: 'EquipmentMenu',
	props: {
		menuData: { type: [String, Array], required: true },
		currentPageId: { type: [String, Number], required: true },
		equipmentPageId: { type: [String, Number], required: true },
		currentParentSlug: { type: String, default: '' },
	},
	data() {
		const isMobileView = this.isMobile();
		return {
			openIndex: null,
			isMobileView,
		};
	},
	computed: {
		menu() {
			// Parse if string
			return typeof this.menuData === 'string'
				? JSON.parse(this.menuData)
				: this.menuData;
		},
	},
	created() {
		this.syncOpenIndex();
	},
	mounted() {
		// Add resize listener
		window.addEventListener('resize', this.handleResize);
	},
	watch: {
		currentPageId() {
			this.syncOpenIndex();
		},
		currentParentSlug() {
			this.syncOpenIndex();
		},
		menuData: {
			handler() {
				this.syncOpenIndex();
			},
			deep: true,
		},
	},
	methods: {
		isMobile() {
			if (typeof window === 'undefined') {
				return false;
			}
			return window.innerWidth <= 768; // Using the same breakpoint as in your SCSS
		},
		syncOpenIndex({ fallbackToFirst = true } = {}) {
			const targetIndex = this.findParentIndexForCurrentPage();

			if (targetIndex !== null) {
				this.openIndex = targetIndex;
			} else if (fallbackToFirst) {
				this.openIndex = this.isMobileView ? null : 0;
			}
		},
		handleResize() {
			const wasMobile = this.isMobileView;
			this.isMobileView = this.isMobile();

			// If switching from desktop to mobile, ensure the current page stays visible
			if (!wasMobile && this.isMobileView) {
				this.syncOpenIndex({ fallbackToFirst: false });
			}
			// If switching from mobile to desktop and no menu is open, attempt to open the current page
			else if (wasMobile && !this.isMobileView && this.openIndex === null) {
				this.syncOpenIndex();
			}
		},
		toggle(idx) {
			this.openIndex = this.openIndex === idx ? null : idx;
		},
		findParentIndexForCurrentPage() {
			if (!this.menu || !this.menu.length) {
				return null;
			}

			const currentId = this.normalizeId(this.currentPageId);
			const equipmentId = this.normalizeId(this.equipmentPageId);

			const matchesId = (item, id) => {
				if (id === null) {
					return false;
				}
				return this.containsPage(item, id);
			};

			for (let idx = 0; idx < this.menu.length; idx += 1) {
				const item = this.menu[idx];
				if (matchesId(item, currentId)) {
					return idx;
				}
			}

			if (equipmentId !== null) {
				for (let idx = 0; idx < this.menu.length; idx += 1) {
					const item = this.menu[idx];
					if (matchesId(item, equipmentId)) {
						return idx;
					}
				}
			}

			if (this.currentParentSlug) {
				const loweredSlug = this.currentParentSlug.toLowerCase();
				const slugIndex = this.menu.findIndex((item) => {
					if (!item) {
						return false;
					}
					if (item.slug) {
						return item.slug.toLowerCase() === loweredSlug;
					}
					if (item.title) {
						return (
							item.title
								.toLowerCase()
								.replace(/\s+/g, '-') === loweredSlug
						);
					}
					return false;
				});

				if (slugIndex !== -1) {
					return slugIndex;
				}
			}

			return null;
		},
		containsPage(item, pageId) {
			if (!item) {
				return false;
			}

			const objectId =
				this.normalizeId(item.object_id || item.objectId || item.id);

			if (objectId !== null && objectId === pageId) {
				return true;
			}

			if (item.children && item.children.length) {
				return item.children.some((child) =>
					this.containsPage(child, pageId),
				);
			}

			return false;
		},
	isCurrentItem(item) {
		const currentId = this.normalizeId(this.currentPageId);
		const itemIdSource =
			item &&
			(item.object_id !== undefined
				? item.object_id
				: item.objectId !== undefined
					? item.objectId
					: item && item.id !== undefined
						? item.id
						: null);
		const itemId = this.normalizeId(itemIdSource);

		if (currentId !== null && itemId !== null) {
			return currentId === itemId;
		}

		const currentSlug = this.currentParentSlug
			? this.currentParentSlug.toLowerCase()
			: null;
		const itemSlug = this.getItemSlug(item);

		if (currentSlug && itemSlug) {
			return itemSlug === currentSlug;
		}

		if (typeof window !== 'undefined' && item && item.url) {
			try {
				const itemUrl = new URL(item.url, window.location.origin);
				const currentUrl = new URL(window.location.href);
				return itemUrl.pathname === currentUrl.pathname;
			} catch (error) {
				// Ignore malformed URLs
			}
		}

		return false;
	},
	getItemSlug(item) {
		if (!item) {
			return null;
		}

		const candidates = [
			item.slug,
			item.post_name,
			item.postName,
			item.path,
			item.url ? this.extractSlugFromUrl(item.url) : null,
			item.title ? this.slugify(item.title) : null,
		].filter((value) => typeof value === 'string' && value.trim().length);

		return candidates.length ? candidates[0].toLowerCase() : null;
	},
	extractSlugFromUrl(url) {
		try {
			const base = typeof window !== 'undefined' ? window.location.origin : '';
			const parsed = new URL(url, base || undefined);
			const parts = parsed.pathname.split('/').filter(Boolean);
			if (parts.length) {
				return parts[parts.length - 1];
			}
		} catch (error) {
			// Ignore invalid URLs
		}
		return null;
	},
	slugify(str) {
		return str
			.toString()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.trim()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '');
	},
	normalizeId(id) {
			if (id === undefined || id === null || id === '') {
				return null;
			}

			const numericId = parseInt(id, 10);

			if (!Number.isNaN(numericId)) {
				return numericId;
			}

			return `${id}`;
		},
	},

	beforeDestroy() {
		// Clean up resize listener
		window.removeEventListener('resize', this.handleResize);
	},
};
</script>

<style lang="scss" scoped>
@import '../../styles/common/variables';

.equipment-menu ul {
	list-style: none;
	padding: 0;
	margin: 0;

	&.submenu {
		border-bottom: 1px solid var(--fpe-blue);
		padding-bottom: 5px;
		margin-bottom: 20px;

		&:last-of-type {
			border-bottom: unset;
			margin-bottom: 0;
			padding-bottom: 0;
		}
	}
}
.parent-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-weight: bold;
	font-size: 1.2em;
	cursor: pointer;
	padding: 0.5em 0;
	border-bottom: 2px solid var(--fpe-blue);
	padding-bottom: 5px;
	margin-bottom: 20px;

	&.hover {
		border-bottom-color: var(--fpe-light-brown);

		.parent-title {
			span {
				color: var(--fpe-light-brown);
			}
		}
	}
}

.parent-title {
	span {
		font-weight: 400;
		font-size: 1.2em;
	}
}

.arrow {
	display: flex;
	margin-right: 0;
	transition: transform 0.2s;
}
.arrow.open {
	transform: rotate(180deg);
}
.child-row {
	border-bottom: 1px solid var(--fpe-blue);
	padding-bottom: 5px;
	margin-bottom: 20px;
}
.submenu {
	padding-left: 0;
	border-bottom: 1px solid var(--fpe-blue);
}
.menu-child {
	padding-left: 0;
}

.subsubmenu {
	padding-bottom: 5px !important;
	margin-bottom: 20px !important;
	border-bottom: 1px solid var(--fpe-blue);
}

.menu-subchild {
	padding-left: 24px;

	&:last-of-type {
		border-bottom: unset;
		margin-bottom: 0;
		padding-bottom: 0;
	}
}

li {
	border-bottom: 1px solid var(--fpe-blue);
	padding-bottom: 5px;
	margin-bottom: 20px;

	&:hover {
		border-bottom: 1px solid var(--fpe-light-brown);
		transition: 0.2s ease all;
	}

	&.menu-child {
		border-bottom: unset;
		margin-bottom: 0;
		padding-bottom: 0;
	}

	&:last-of-type {
		border-bottom: unset;
		/* margin-bottom: 0; */
		padding-bottom: 0;
	}

	&.menu-parent {
		border-bottom: unset;
		margin-bottom: 0;
		padding-bottom: 0;
	}

	a {
		padding: 0;
		border-right: 0;
		border: none;
		position: relative;
		display: flex;
		align-items: center;
		gap: 12px;
		/* text-transform: uppercase; */
		color: var(--fpe-blue);
		text-decoration: none;
		position: relative;
		overflow: hidden;
		font: normal normal 300 14px/18px var(--primary-font-family);

		span {
			transition: 0.2s ease all;
			font: normal normal 300 14px/18px var(--primary-font-family);
			position: relative;
			left: 0;
		}

		@media (max-width: $mobile-xl) {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		&:hover,
		&.hover {
			span {
				position: relative;
				left: -21px;
				transition: 0.2s ease all;
				color: var(--fpe-light-brown);
			}

			&::before {
				opacity: 0;
				transition: 0.2s ease all;
				transform: translateX(-100%);
				visibility: hidden;
			}

			&::after {
				opacity: 1;
				transition: 0.2s ease all;
				transform: translateX(-21px);
				visibility: visible;
			}
		}

		&::before {
			content: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAuODM0Njk3IDEwTDAgOS4xNzg5OEw2Ljg1NzYxIDIuMjgyNDNMMi4zMDc2OSAxLjIxNTExTDIuNTY5NTYgMEw4LjUxMDY0IDEuNDYxNDFMMTAgNy41MDQxMUw4Ljc3MjUgNy43ODMyNUw3LjY5MjMxIDMuMTE5ODdMMC44MzQ2OTcgMTBaIiBmaWxsPSIjNTM2MjhCIi8+Cjwvc3ZnPgo=');
			/* width: 6.11px;
                height: 6.09px;
                background: var(--cobalt); */
			opacity: 1;
			transition: 0.2s ease all;
			transform: translateX(0);
			visibility: visible;
		}

		&::after {
			content: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAuODM0Njk3IDEwTDAgOS4xNzg5OEw2Ljg1NzYxIDIuMjgyNDNMMi4zMDc2OSAxLjIxNTExTDIuNTY5NTYgMEw4LjUxMDY0IDEuNDYxNDFMMTAgNy41MDQxMUw4Ljc3MjUgNy43ODMyNUw3LjY5MjMxIDMuMTE5ODdMMC44MzQ2OTcgMTBaIiBmaWxsPSIjOWQ4ODc5Ii8+Cjwvc3ZnPgo=');
			/* width: 6.11px;
                height: 6.09px;
                background: var(--cobalt); */
			opacity: 0;
			transition: 0.2s ease all;
			transform: translateX(0);
			visibility: hidden;
		}
	}
}
</style> 


// WEBPACK FOOTER //
// resources/assets/scripts/vue/EquipmentMenu.vue